import { useState, useEffect } from 'react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { SocialProjectsModule } from '@/components/superadmin/SocialProjectsModule'
import { TreeVisualization } from '@/components/customer/TreeVisualization'
import { CityVisualization } from '@/components/customer/CityVisualization'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { Heart, Trees, Home, Building, BarChart3, TrendingUp } from 'lucide-react'

interface GlobalProjectSummary {
  social_project_id: string
  project_name: string
  project_type: 'trees' | 'family_help'
  total_contributions: number
  active_tenants: number
  total_customers: number
}

interface TenantProjectData {
  tenant_id: string
  tenant_name: string
  tenant_slug: string
  projects: {
    social_project_id: string
    project_name: string
    project_type: string
    contribution_count: number
    customer_count: number
  }[]
}

const SuperadminSocialEngagement = () => {
  const { toast } = useToast()
  const [globalSummaries, setGlobalSummaries] = useState<GlobalProjectSummary[]>([])
  const [tenantData, setTenantData] = useState<TenantProjectData[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchGlobalData()
  }, [])

  const fetchGlobalData = async () => {
    try {
      // Get all social projects
      const { data: socialProjects, error: projectsError } = await supabase
        .from('social_projects')
        .select('*')
        .eq('is_active', true)

      if (projectsError) throw projectsError

      // Get global summaries for each project
      const summaries: GlobalProjectSummary[] = []
      
      for (const project of socialProjects || []) {
        // Get total contributions across all tenants
        const { data: allContributions, error: contributionsError } = await supabase
          .from('customer_social_contributions')
          .select('contribution_count, customer:customers!inner(tenant_id)')
          .eq('social_project_id', project.id)

        if (contributionsError) throw contributionsError

        const totalContributions = allContributions?.reduce((sum, c) => sum + c.contribution_count, 0) || 0
        const uniqueTenants = new Set(allContributions?.map(c => c.customer.tenant_id)).size
        const totalCustomers = allContributions?.length || 0

        summaries.push({
          social_project_id: project.id,
          project_name: project.name,
          project_type: project.project_type as 'trees' | 'family_help',
          total_contributions: totalContributions,
          active_tenants: uniqueTenants,
          total_customers: totalCustomers
        })
      }

      // Get tenant-specific data
      const { data: tenantProjects, error: tenantProjectsError } = await supabase
        .from('tenant_social_projects')
        .select(`
          tenant_id,
          social_project_id,
          tenant:tenants!inner (
            name,
            slug
          ),
          social_project:social_projects!inner (
            name,
            project_type
          )
        `)
        .eq('is_enabled', true)

      if (tenantProjectsError) throw tenantProjectsError

      // Group by tenant and get contribution data
      const tenantMap = new Map<string, TenantProjectData>()
      
      for (const tp of tenantProjects || []) {
        const tenantId = tp.tenant_id
        
        if (!tenantMap.has(tenantId)) {
          tenantMap.set(tenantId, {
            tenant_id: tenantId,
            tenant_name: (tp.tenant as any).name,
            tenant_slug: (tp.tenant as any).slug,
            projects: []
          })
        }

        // Get contributions for this tenant-project combination
        const { data: contributions, error: contribError } = await supabase
          .from('customer_social_contributions')
          .select(`
            contribution_count,
            customer:customers!inner (
              tenant_id
            )
          `)
          .eq('social_project_id', tp.social_project_id)
          .eq('customer.tenant_id', tenantId)

        if (contribError) {
          console.error('Error fetching contributions:', contribError)
          continue
        }

        const contributionCount = contributions?.reduce((sum, c) => sum + c.contribution_count, 0) || 0
        const customerCount = contributions?.length || 0

        tenantMap.get(tenantId)!.projects.push({
          social_project_id: tp.social_project_id,
          project_name: (tp.social_project as any).name,
          project_type: (tp.social_project as any).project_type,
          contribution_count: contributionCount,
          customer_count: customerCount
        })
      }

      setGlobalSummaries(summaries)
      setTenantData(Array.from(tenantMap.values()))
    } catch (error) {
      console.error('Error fetching global data:', error)
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'Daten konnten nicht geladen werden.'
      })
    } finally {
      setLoading(false)
    }
  }

  const getProjectIcon = (type: string) => {
    switch (type) {
      case 'trees': return <Trees className="h-5 w-5 text-green-600" />
      case 'family_help': return <Heart className="h-5 w-5 text-red-600" />
      default: return <Heart className="h-5 w-5 text-primary" />
    }
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">Laden...</div>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Heart className="h-8 w-8 text-primary" />
            Soziales Engagement
          </h2>
          <p className="text-muted-foreground">
            Verwalten Sie soziale Projekte und sehen Sie globale Statistiken
          </p>
        </div>
        
        {/* Project Management */}
        <SocialProjectsModule />

        {/* Global Overview */}
        {globalSummaries.length > 0 && (
          <>
            <div>
              <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <TrendingUp className="h-6 w-6" />
                Globale Übersicht
              </h3>
              
              {/* Global Visualizations */}
              <div className="grid gap-6 lg:grid-cols-1 xl:grid-cols-2 mb-6">
                {globalSummaries.map((summary) => (
                  <div key={summary.social_project_id}>
                    {summary.project_type === 'trees' && (
                      <TreeVisualization
                        totalTrees={summary.total_contributions}
                        userTrees={0} // Global view shows all trees
                        projectName={`${summary.project_name} - ${summary.active_tenants} Unternehmen`}
                      />
                    )}
                    
                    {summary.project_type === 'family_help' && (
                      <CityVisualization
                        totalHouses={summary.total_contributions}
                        userHouses={0} // Global view shows all houses
                        projectName={`${summary.project_name} - ${summary.active_tenants} Unternehmen`}
                      />
                    )}
                  </div>
                ))}
              </div>

              {/* Global Statistics */}
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mb-6">
                {globalSummaries.map((summary) => (
                  <Card key={summary.social_project_id} className="border-primary/20">
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-2">
                        {getProjectIcon(summary.project_type)}
                        <CardTitle className="text-lg">
                          {summary.project_name}
                        </CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">Global:</span>
                          <Badge variant="default" className="text-lg font-bold">
                            {summary.total_contributions}
                          </Badge>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">Unternehmen:</span>
                          <div className="flex items-center gap-1">
                            <Building className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{summary.active_tenants}</span>
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">Kunden:</span>
                          <span className="font-medium">{summary.total_customers}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Tenant Details */}
              {tenantData.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5" />
                      Unternehmen im Detail
                    </CardTitle>
                    <CardDescription>
                      Beiträge der einzelnen Unternehmen zu den sozialen Projekten
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      {tenantData.map((tenant) => (
                        <div key={tenant.tenant_id} className="border rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-4">
                            <Building className="h-5 w-5 text-muted-foreground" />
                            <h4 className="font-medium text-lg">{tenant.tenant_name}</h4>
                            <Badge variant="outline">{tenant.tenant_slug}</Badge>
                          </div>
                          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                            {tenant.projects.map((project) => (
                              <div key={project.social_project_id} className="bg-muted/30 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-2">
                                  {getProjectIcon(project.project_type)}
                                  <span className="font-medium">{project.project_name}</span>
                                </div>
                                <div className="space-y-1">
                                  <div className="flex justify-between items-center text-sm">
                                    <span className="text-muted-foreground">Beiträge:</span>
                                    <Badge variant="secondary">{project.contribution_count}</Badge>
                                  </div>
                                  <div className="flex justify-between items-center text-sm">
                                    <span className="text-muted-foreground">Kunden:</span>
                                    <span>{project.customer_count}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  )
}

export default SuperadminSocialEngagement